<template>
  <div class="card-container">
    <a
      class="card"
      :class="{ 'mobile-active': isMobileActive, 'card--small': isSmall, 'card--with-background': hasBackgroundImage }"
      :href="projectHref"
      :aria-label="`Abrir obra ${cardData.name}`"
      @touchstart="handleTouchStart"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div v-if="hasBackgroundImage" class="background-gradient"></div>
      <div v-if="isZinnesOriginal" class="original absolute">
        <img :src="originalSeal" alt="original">
      </div>
      <v-chip v-else-if="getLabel(cardData) != null" :color="getColor(cardData)" dark label class="absolute">
        <b>{{ getLabel(cardData) }}</b>
      </v-chip>
      <div class="image-cover" :class="{ 'image-cover--with-padding': hasBackgroundImage }">
        <img
          loading="lazy"
          :src="cardData.project_thumb"
          @contextmenu.prevent
          @error="handleImageError"
          :alt="cardData.name"
          :style="{
            transition: 'opacity 0.3s ease',
            opacity: imageLoaded ? 1 : 0
          }"
          @load="imageLoaded = true"
        />
        <div
          v-if="!imageLoaded"
          class="image-placeholder"
          :style="{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, #2b2b2b 25%, #3b3b3b 50%, #2b2b2b 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.5s infinite linear'
          }"
        ></div>
        <div class="overlay" :class="{ 'overlay--with-margin': hasBackgroundImage }">
          <div class="details-card">
            <h3 class="title-project">Sinopse</h3>
            <p>{{ cardData.description }}</p>
          </div>
          <div class="footer-card">
            <div class="footer-column left">
              <div class="stat-row">
                <v-icon color="white" size="21">mdi-thumb-up</v-icon>
                <span>{{ formatNumber(cardData.likes_count) }}</span>
              </div>
              <div class="stat-row">
                <v-icon color="white" size="21">mdi-eye</v-icon>
                <span>{{ formatNumber(cardData.views) }}</span>
              </div>
            </div>
            <div class="footer-column right">
              <div class="stat-row">
                <span>{{ formatNumber(getCommentsCount(cardData)) }}</span>
                <v-icon color="white" size="21">mdi-message-text</v-icon>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-title-overlay" :class="{ 'card-title-overlay--below': hasBackgroundImage }">
        <h1 ref="title" :class="{ marquee: isOverflowing }">
          {{ cardData.name }}
        </h1>
        <div class="card-meta-row">
          <span>{{ cardData.language.name }}  {{ cardData.gender.name }}</span>
          <span
            v-if="cardData.age_rating"
            class="card-age-rating"
            :style="{ backgroundColor: ratingColor(cardData.age_rating.age), color: cardData.age_rating.age === 12 ? '#000' : '#fff' }"
          >{{ cardData.age_rating.rating }}</span>
          <span v-else class="card-age-rating" style="background-color: #2e7d32; color: #fff;">Livre</span>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import moment from "moment";
import { ref, onMounted, nextTick, onBeforeUnmount } from "vue";
import originalSeal from "@/assets/images/original3.png";
import { projectPath } from "@/utils/projectRoutes";

export default {
  name: "SimpleCardTest",
  props: {
    cardData: {
      type: Object,
      required: true,
      default: () => ({
        name: '',
        description: '',
        category: { name: '' },
        type: { name: '' },
        language: { name: '' },
        gender: { name: '' },
        age_rating: null,
        likes_count: 0,
        views: 0,
        completed: false,
        titles: [],
        project_thumb: '',
        id: null,
      })
    },
    isSmall: {
      type: Boolean,
      default: false
    },
    hasBackgroundImage: {
      type: Boolean,
      default: false
    },
    isZinnesOriginal: {
      type: Boolean,
      default: false
    }
  },

  setup() {
    const title = ref(null);
    const isOverflowing = ref(false);
    const imageLoaded = ref(false);
    const isMobileActive = ref(false);
    let touchTimer = null;
    let startX = 0;
    let startY = 0;

    const clearTouchLogic = () => {
      if (touchTimer) clearTimeout(touchTimer);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };

    const handleGlobalTouchMove = (e) => {
      const moveX = Math.abs(e.touches[0].clientX - startX);
      const moveY = Math.abs(e.touches[0].clientY - startY);

      // Sensitive threshold: 5px movement cancels the long press
      if (moveX > 5 || moveY > 5) {
        clearTouchLogic();
        isMobileActive.value = false;
      }
    };

    const handleGlobalTouchEnd = () => {
      clearTouchLogic();
      isMobileActive.value = false;
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 1) return;

      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;

      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
      document.addEventListener('touchend', handleGlobalTouchEnd);
      document.addEventListener('touchcancel', handleGlobalTouchEnd);

      touchTimer = setTimeout(() => {
        isMobileActive.value = true;
      }, 1000);
    };

    // Desktop Hover Logic
    const handleMouseEnter = () => {
      if (!('ontouchstart' in window) || navigator.maxTouchPoints === 0) {
        isMobileActive.value = true;
      }
    };

    const handleMouseLeave = () => {
      if (!('ontouchstart' in window) || navigator.maxTouchPoints === 0) {
        isMobileActive.value = false;
      }
    };

    onBeforeUnmount(() => {
      clearTouchLogic();
    });

    onMounted(() => {
      nextTick(() => {
        if (title.value) {
          isOverflowing.value = title.value.scrollWidth > title.value.clientWidth;
        }
      });
    });

    return {
      title,
      isOverflowing,
      imageLoaded,
      isMobileActive,
      originalSeal,
      handleTouchStart,
      handleMouseEnter,
      handleMouseLeave
    };
  },
  data() {
    return {
      placeholderImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU4IiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDI1OCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI1OCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiMyYjJiMmIiLz4KPHBhdGggZD0iTTEyOSAxNTBDMTQ0LjE2NCAxNTAgMTU2IDE2MS44MzYgMTU2IDE3N0MxNTYgMTkyLjE2NCAxNDQuMTY0IDIwNCAxMjkgMjA0QzExMy44MzYgMjA0IDEwMiAxOTIuMTY0IDEwMiAxNzdDMTAyIDE2MS44MzYgMTEzLjgzNiAxNTAgMTI5IDE1MFoiIGZpbGw9IiM0YjRiNGIiLz4KPHBhdGggZD0iODAgMjUwSDgwVjI0NkM3OS45OTk4IDIzNC4zNTcgODkuMzU3NCAyMjUgMTAxIDIyNUgxNTdDMTY4LjY0MyAyMjUgMTc4IDIzNC4zNTcgMTc4IDI0NlYyNTBIMDhaIiBmaWxsPSIjNGI0YjRiIi8+Cjwvc3ZnPgo==',
    };
  },
  computed: {
    projectHref() {
      return projectPath(this.cardData);
    },
  },
  methods: {
    formatNumber(num) {
      if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
      if (num >= 1_000) return (num / 1_000).toFixed(1) + "k";
      return num;
    },
    getColor(item) {
      if (!item) return 'grey';
      if (item.completed) return 'orange';
      if (item.titles?.length > 1) return 'var(--zinnes-primary)';
      if (item.titles?.length === 1) return 'red';
      return 'grey';
    },
    getLabel(item) {
      if (!item) return null;
      if (item.completed) return 'Completo';
      if (item.titles?.length > 1 && this.isRecent(item.titles[item.titles.length-1].created_at)) return 'Atualizado';
      if (item.titles?.length === 1 && this.isRecent(item.titles[0].created_at)) return 'Novo';
      return null;
    },
    getCommentsCount(item) {
      if (!item) return 0;
      if (Array.isArray(item.titles) && item.titles.length > 0) {
        return item.titles.reduce((sum, title) => sum + (Number(title.comments) || 0), 0);
      }
      return Number(item.comments_count) || 0;
    },
    isRecent(publishedAt) {
      const now = moment();
      const oneWeekAgo = moment().subtract(7, 'days');
      const pubDate = moment(publishedAt);

      return pubDate.isBetween(oneWeekAgo, now, null, '[]'); // inclusivo
    },

    handleImageError(event) {
      const img = event.target;
      img.src = this.placeholderImage;
      this.imageLoaded = true;
    },
    ratingColor(age) {
      if (age <= 10) return '#0095DA';
      if (age <= 12) return '#FBC115';
      if (age <= 14) return '#F58220';
      return '#EC1D25';
    },
  },
};
</script>

<style scoped>
.card-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.85);
}

.card-age-rating {
  background-color: rgba(101,31,141,0.85);
  color: #fff;
  font-weight: 700;
  font-size: 0.7rem;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
}

.absolute {
  position: absolute;
  top: 12px;
  right: 22px;
  z-index: 3;
}

.original {
  position: absolute;
  top: 4px;
  right: 14px;
  width: 70px;
  height: 90px;
  color: white;
  opacity: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: opacity 0.3s ease;
  z-index: 3;
}

.original img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
}

h1,
h4 {
  color: #fff !important;
}

h1 {
  font-size: x-large;
  font-weight: 600;
}

h4 {
  font-weight: 400;
  font-size: 14px;
}


.v-application ul,
.v-application ol {
  padding-left: 0px;
}

.rating-container {
  display: flex;
  gap: 12px;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
}


p {
  color: #fff !important;
}

.card {
  position: relative;
  display: block;
  width: 258px;
  height: 400px;
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 7px 8px #404040;
  margin: 0px;
  border: 2px solid #888888;
  color: inherit;
  text-decoration: none;
}

.card--small {
  width: 240px;
  height: 380px;
  margin: 0px;
}

.card--with-background {
  box-shadow: none;
  border: none;
  display: flex;
  flex-direction: column;
}

.background-gradient {
  position: absolute;
  top: 25%;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, #666666 0%, #333333 50%, #000000 100%);
  border-radius: 4px;
  pointer-events: none;
  z-index: 1;
}

@media (hover: hover) and (pointer: fine) {
  .card:hover {
    transform: scale(1.02) translateY(-4px);
    box-shadow: 10px 11px #404040;
  }

  .card--with-background:hover {
    transform: scale(1.02) translateY(-4px);
    box-shadow: none;
  }

  .card--with-background:hover .overlay {
    opacity: 1;
    display: flex;
  }
  /* Removed CSS-based overlay hover */
}

/* Active state (controlled by JS for both Mobile Long-Press and Desktop Hover) */
.card.mobile-active {
  transform: scale(1.02) translateY(-4px);
  box-shadow: 10px 11px #404040;
}

.card--with-background.mobile-active {
  box-shadow: none;
}

.card.mobile-active .overlay {
  opacity: 1 !important;
}

.card.mobile-active .v-chip {
  opacity: 0 !important;
  transition: opacity 0.3s ease;
}

.card.mobile-active .original {
  opacity: 0 !important;
  transition: opacity 0.3s ease;
}

@media (max-width: 768px) {
  /* Ensure CSS hover never triggers overlay on mobile */
  .card:hover .overlay {
    opacity: 0;
  }

  .card:hover .card-title-overlay {
    opacity: 1;
  }

  .card:hover .v-chip {
    opacity: 1;
  }
}

.card-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.7) 50%, rgba(0, 0, 0, 0.3) 80%, transparent 100%);
  padding: 16px 12px 12px 12px;
  z-index: 2;
  transition: opacity 0.3s ease;
}

.card-title-overlay--below {
  position: relative;
  bottom: auto;
  left: auto;
  right: auto;
  background: transparent;
  padding: 12px;
  z-index: 2;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

@media (max-width: 395px) {
  .card-title-overlay--below {
    padding: 4px;
  }
}

.card-title-overlay h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-title-overlay h4 {
  font-size: 11px;
  font-weight: 500;
  margin: 0;
  color: #FFDFAE !important;
  letter-spacing: 1px;
  text-transform: uppercase;
  word-spacing: 8px;
}

.image-cover {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  flex: 1;
  z-index: 2;
}

.image-cover--with-padding {
  padding: 0 10px;
  height: 338px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.image-cover--with-padding img {
  width: 230px !important;
  height: 330px !important;
  min-width: 230px;
  min-height: 330px;
  max-width: 230px;
  max-height: 330px;
  object-fit: cover;
  border: 4px solid #000;
  border-radius: 4px;
}

.image-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
}


.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  opacity: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px 20px 5px 20px;
  transition: opacity 0.3s ease;
  z-index: 3;
}

.card--with-background .overlay {
  opacity: 0;
}

.overlay--with-margin {
  width: 230px;
  height: 330px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.title-project {
  font-size: 16px;
  font-weight: bold;
  text-align: center;
}

.details-card {
  font-size: 14px;
  color: #ccc;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  padding: 5px;
}

.card.mobile-active .details-card {
  max-height: 500px;
  opacity: 1;
}

.list-genere {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 5px;
}

.list-genere li {
  list-style: none;
  padding: 4px 10px;
  background-color: var(--zinnes-primary);
  border-radius: 5px;
  font-size: 12px;
}

.footer-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 5px 5% 0 5%;
}

.footer-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-column.left {
  align-items: flex-start;
}

.footer-column.right {
  align-items: flex-end;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 13.5px;
  font-weight: 600;
}

.stat-row v-icon {
  opacity: 0.9;
}

.rating {
  display: flex;
  align-items: center;
  gap: 2px;
}

.custom-icon {
  color: #ffffff !important;
}


@media (max-width: 520px) {
  h1 {
    font-size: 8px;
  }

  h4 {
    font-size: 12px;
  }

  .card {
    width: 120px !important;
    height: 220px !important;
  }

  .original {
    position: absolute;
    top: 0;
    right: 14px;
    width: 20px;
    height: 40px;
    z-index: 3;
  }

  .absolute ::v-deep .v-chip__content {
    font-size: 12px !important;
    padding: 0 0px;
  }

  .absolute ::v-deep .v-chip,
  .absolute ::v-deep .v-chip.v-size--default {
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
    border-radius: 4px !important;
    font-size: 10px !important;
    padding: 0 8px !important;
  }

  .v-chip.v-size--default {
    border-radius: 4px !important;
    font-size: 10px !important;
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }

  .title h3 {
    font-size: 12px;
  }

  .footer-card {
    flex-direction: column;
    align-items: center;
  }

  .rating {
    width: auto;
    height: 20px;
  }

  .custom-icon {
    font-size: 14px;
    color: var(--zinnes-primary) !important;
  }
}

@media (max-width: 1024px) {
  .card {
    width: 130px;
    height: 300px;
  }

  .card-title-overlay h1 {
    font-size: 16px;
  }

  .card-title-overlay h4 {
    font-size: 10px;
  }

  .title-project {
    font-size: 14px;
  }

  .list-genere li {
    font-size: 10px;
    padding: 3px 8px;
  }

  .footer-card {
    font-size: 12px;
  }

  .custom-icon {
    font-size: 16px;
  }
}

@media (max-width: 1440px) {
  .card {
    width: 230px;
    height: 380px;
  }

  .card-title-overlay h1 {
    font-size: 19px;
  }

  .card-title-overlay h4 {
    font-size: 11px;
  }
}

@media (max-width: 768px) {
  h1 {
    font-size: 18px;
  }

  .card {
    width: 129px !important;
    height: 237px !important;
  }

    .original {
      position: absolute;
      top: 0;
      right: 4px;
      width: 20px;
      height: 40px;
      z-index: 3;
    }

  .card--with-background {
    display: flex;
    flex-direction: column;
  }

  .image-cover--with-padding {
    height: 190px;
    padding: 0 6px;
  }

  .image-cover--with-padding img {
    width: 100% !important;
    height: 100% !important;
    min-width: 100%;
    min-height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .overlay--with-margin {
    width: calc(100% - 12px);
    height: 190px;
  }

  .absolute {
    position: absolute;
    top: 6px;
    right: 10px;
    z-index: 3;
  }

  .absolute ::v-deep .v-chip__content {
    font-size: 12px !important;
    padding: 0 0px;
  }

  .absolute ::v-deep .v-chip,
  .absolute ::v-deep .v-chip.v-size--default {
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
    border-radius: 4px !important;
    font-size: 10px !important;
    padding: 0 8px !important;
  }

  .v-chip.v-size--default {
    border-radius: 4px !important;
    font-size: 10px !important;
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }

  .title-project {
    font-size: 12px;
  }

  .list-genere li {
    font-size: 9px;
    padding: 2px 6px;
  }

  /* Mobile footer adjustments - further reduced to prevent overlap */
  .overlay {
    padding-bottom: 5px;
  }

  .footer-card {
    padding: 1px 0 0 0; /* Minimal padding */
    flex-direction: row !important; /* Force row layout */
  }

  .footer-column {
    gap: 2px; /* Reduce vertical gap between like/view */
  }

  .stat-row {
    font-size: 9px; /* Reduced from 12px */
    gap: 2px; /* Reduced from 4px */
  }

  .stat-row .v-icon {
    font-size: 13.5px !important; /* Reduced from 18px */
  }
}

@media (max-width: 600px) {
  .card {
    width: 129px !important;
    height: 237px !important;
  }

    .original {
      position: absolute;
      top: 0;
      right: 14px;
      width: 20px;
      height: 40px;
      z-index: 3;
    }

  .card--with-background {
    display: flex;
    flex-direction: column;
  }

  .image-cover--with-padding {
    height: 190px;
    padding: 0 6px;
  }

  .image-cover--with-padding img {
    width: 100% !important;
    height: 100% !important;
    min-width: 100%;
    min-height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
  }

  .overlay--with-margin {
    width: calc(100% - 12px);
    height: 190px;
  }

  .absolute ::v-deep .v-chip__content {
   font-size: 9px !important;
    padding: 0 0px;
  }

  .absolute ::v-deep .v-chip,
  .absolute ::v-deep .v-chip.v-size--default {
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
    border-radius: 4px !important;
    font-size: 10px !important;
    padding: 0 8px !important;
  }

  .v-chip.v-size--default {
    border-radius: 4px !important;
    font-size: 10px !important;
    height: 18px !important;
    min-height: 18px !important;
    max-height: 18px !important;
    line-height: 18px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }

  .title-project {
   font-size: 9px;
  }

  .list-genere li {
    font-size: 8px;
    padding: 2px 5px;
  }

  .footer-card {
    font-size: 10px;
  }

  .custom-icon {
    font-size: 12px;
  }
}

@media (max-width: 425px) {
  .card {
    width: 150px !important;
     font-size: 11px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }
}

@media (max-width: 375px) {
    .card {
    font-size: 7px;
    width: 129px !important;
    height: 237px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }
}

@media (max-width: 395px) {
  .card {
   width: 134px !important;
    height: 237px !important;
  }

  .card-title-overlay h1 {
   font-size: 10px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }
}

@media (max-width: 350px) {
  .card {
    width: 129px !important;
    height: 237px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }
}

@media (max-width: 320px) {
  .card {
    width: 129px !important;
    height: 237px !important;
  }

  .card-title-overlay h1 {
    font-size: 12px;
  }

  .card-title-overlay h4 {
    font-size: 7px;
  }
}

@media (min-width: 2551px) {
  .card {
    width: 291px;
    height: 452px;
    border-width: 3px;
  }

  .card--small {
    width: 271px;
    height: 429px;
    border-width: 3px;
  }

  .image-cover--with-padding {
    height: 381px;
  }

  .image-cover--with-padding img {
    width: 259px !important;
    height: 373px !important;
    min-width: 259px;
    min-height: 373px;
    max-width: 259px;
    max-height: 373px;
    border-width: 5px;
  }

  .overlay--with-margin {
    width: 259px;
    height: 373px;
  }

  .overlay {
    padding: 23px 23px 6px 23px;
  }

  .card:hover {
    box-shadow: 12px 13px #404040;
  }
}


/* Animação de skeleton loading */
@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

/* Otimização de performance */
.image-cover {
  position: relative;
  contain: layout style paint;
}
</style>
